import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { uploadSalesData, downloadTemplate } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Upload as UploadIcon, 
  FileSpreadsheet, 
  Download, 
  CheckCircle2, 
  XCircle,
  AlertTriangle,
  FileUp
} from "lucide-react";
import { Navigate } from "react-router-dom";

const Upload = () => {
  const { isAdmin } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const filename = selectedFile.name.toLowerCase();
      if (filename.endsWith('.csv') || filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
        setFile(selectedFile);
        setResult(null);
      } else {
        toast.error("Please select a CSV or Excel file");
        e.target.value = "";
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);
    try {
      const response = await uploadSalesData(file);
      setResult(response.data);
      if (response.data.success > 0) {
        toast.success(`Successfully imported ${response.data.success} records`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} records failed to import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadTemplate();
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sales_upload_template.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="upload-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-3xl text-slate-900">Upload Historical Data</h1>
        <p className="text-muted-foreground mt-1">Import sales data from CSV or Excel files</p>
      </div>

      {/* Instructions Card */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary" />
            File Format Instructions
          </CardTitle>
          <CardDescription>
            Your file should contain the following columns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Date *</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Salon Name *</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Opening Balance</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Service Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Product Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Membership Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Misc Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Card Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Male Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Male Walk-ins</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Female Sale</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Female Walk-ins</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Cash Expenses</div>
            <div className="bg-muted/50 px-3 py-2 rounded-lg">Bank Deposit</div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">* Required fields. Date format: YYYY-MM-DD (e.g., 2024-01-15)</p>
          
          <Button 
            variant="outline" 
            onClick={handleDownloadTemplate} 
            className="mt-4 rounded-full"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Card */}
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <UploadIcon className="w-5 h-5 text-primary" />
            Upload File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!result ? (
            <>
              <div className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/50 transition-colors">
                <FileUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <Label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-primary font-medium hover:underline">Click to select</span>
                  <span className="text-muted-foreground"> or drag and drop</span>
                </Label>
                <Input
                  id="file-upload"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="file-input"
                />
                <p className="text-xs text-muted-foreground mt-2">CSV or Excel files only</p>
              </div>

              {file && (
                <div className="flex items-center justify-between bg-muted/50 px-4 py-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-primary" />
                    <span className="font-medium">{file.name}</span>
                    <span className="text-sm text-muted-foreground">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFile(null)}
                    className="text-muted-foreground hover:text-red-600"
                  >
                    Remove
                  </Button>
                </div>
              )}

              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full rounded-full bg-orange-500 hover:bg-orange-600"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-4 h-4 mr-2" />
                    Upload & Import
                  </>
                )}
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              {/* Result Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{result.success}</p>
                  <p className="text-sm text-green-600">Records Imported</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{result.failed}</p>
                  <p className="text-sm text-red-600">Records Failed</p>
                </div>
              </div>

              {/* Errors List */}
              {result.errors && result.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-800">Errors ({result.errors.length})</span>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {result.errors.map((error, idx) => (
                      <p key={idx} className="text-sm text-amber-700 bg-amber-100 px-3 py-1 rounded">
                        {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={resetUpload}
                variant="outline"
                className="w-full rounded-full"
                data-testid="upload-another-btn"
              >
                Upload Another File
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Upload;
